import { useMemo } from 'react';
import {
  keepPreviousData,
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import {
  WalletApiAdapter,
  type WalletTransactionsFilters,
} from '../adapters/WalletApiAdapter';
import {
  WalletDetail,
  WalletTransactionsResponse,
} from '../models/Wallet';

export const useWallet = (walletId?: number) => {
  const httpClient = useMemo(() => createHttpClient(apiClientWH), []);
  const apiAdapter = useMemo(
    () => new WalletApiAdapter(httpClient),
    [httpClient]
  );

  return useQuery<WalletDetail, Error>({
    queryKey: ['wallet-detail', walletId],
    enabled: typeof walletId === 'number' && !Number.isNaN(walletId),
    queryFn: () => apiAdapter.getWalletDetail(walletId as number),
    staleTime: 5 * 60 * 1000,
  });
};

const DEFAULT_TRANSACTIONS_FILTERS: WalletTransactionsFilters = {};

export const useWalletTransactions = (
  walletId?: number,
  filters?: WalletTransactionsFilters
) => {
  const httpClient = useMemo(() => createHttpClient(apiClientWH), []);
  const apiAdapter = useMemo(
    () => new WalletApiAdapter(httpClient),
    [httpClient]
  );
  const resolvedFilters = filters ?? DEFAULT_TRANSACTIONS_FILTERS;
  const filtersKey =
    resolvedFilters && Object.keys(resolvedFilters).length > 0
      ? JSON.stringify(resolvedFilters)
      : 'no-filters';

  return useInfiniteQuery<WalletTransactionsResponse, Error>({
    queryKey: ['wallet-transactions', walletId, filtersKey],
    enabled: typeof walletId === 'number' && !Number.isNaN(walletId),
    queryFn: ({ pageParam }) =>
      apiAdapter.getWalletTransactions(walletId as number, {
        ...resolvedFilters,
        page: pageParam as number,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      if (lastPage.meta.currentPage < lastPage.meta.lastPage) {
        return lastPage.meta.currentPage + 1;
      }
      return undefined;
    },
    staleTime: 60 * 1000,
    placeholderData: keepPreviousData,
  });
};
